import React, { useState, useRef, useEffect } from "react";
import apiHelper from "../../apiHelper";
import { useLoader } from "../../components/GlobalLoader";
import {
  Search,
  MoreHorizontal,
  ChevronDown,
  LinkIcon,
  X,
  Copy,
  Upload,
} from "lucide-react";
import { useHistory, useLocation } from "react-router-dom";
import SortableHeader from "../../components/SortableHeader";
import noBrandImage from '../../assets/img/brand/no-brand-img.png';
import noStockInstanceImage from '../../assets/img/emptyStockInstance.png';
import PlatformOrderDrawer from "../../components/PlatformOrderDrawer";
import Pagination from "../../components/Pagination";
import emptySearchImg from "../../assets/img/brand/empty-search.png";
import FilterDrawer from "../../components/FilterDrawer";
import ExportOrdersDrawer from "../../components/ExportOrdersDrawer";
import AlertNotification from "../../components/AlertNotification";
import ToLinkCutoffTooltip from "../../components/ToLinkCutoffTooltip";
import { formatFulfillmentMethod } from "../../utils/helper";
import noImage from '../../assets/img/brand/noImage.png';

const platformConfig = {
  '/platform-order-shopee': {
    label: 'Shopee SG',
    platformName: 'Shopee SG',
  },
  '/platform-order-lazada': {
    label: 'Lazada SG',
    platformName: 'Lazada SG',
  },
  '/platform-order-tiktok': {
    label: 'TikTok SG',
    platformName: 'TikTok SG',
  }
};

const PAGE_SIZE = 20;

// Order Status choices for the export drawer. Fixed rather than derived from the loaded
// rows so every status the platform-order module can hold is offered, including ones with
// no matching orders right now — an empty export is a valid answer, an unofferable status
// is not. The wording is exactly what the sync commands write to order_status.status_name
// (STATUS_NAME_MAP in scan_shopee_orders / scan_lazada_orders / scan_tiktok_orders), which
// is what the export filters on. Unpaid is left out on purpose: it is visible under its own
// tab and under All, but the export view still skips unpaid orders.
const EXPORT_STATUS_OPTIONS = ['To Process', 'Processed', 'Shipping', 'Completed', 'Cancelled'];
// TikTok's two pre-shipment states are labelled one step "earlier" than the other
// platforms' (2026-09-10): its ON_HOLD is stored as "Pending" and AWAITING_SHIPMENT /
// AWAITING_COLLECTION as "To Process"; it never holds "Processed". Same DB
// wording rule as above — these are the exact status_name values for TikTok rows.
const TIKTOK_STATUS_OPTIONS = ['Pending', 'To Process', 'Shipping', 'Completed', 'Cancelled'];
const isTikTokPlatform = (platformName) => (platformName || '').includes('TikTok');
const statusOptionsFor = (platformName) =>
  isTikTokPlatform(platformName) ? TIKTOK_STATUS_OPTIONS : EXPORT_STATUS_OPTIONS;
// The To Ship tab's sub-statuses in display order; the first is the default selection.
// Keys are what the API's to_ship_status param and the counts endpoint use.
const toShipSubStatusesFor = (platformName) =>
  isTikTokPlatform(platformName)
    ? [{ key: 'pending', label: 'Pending' }, { key: 'to-process', label: 'To Process' }]
    : [{ key: 'to-process', label: 'To Process' }, { key: 'processed', label: 'Processed' }];
const defaultToShipStatusFor = (platformName) => toShipSubStatusesFor(platformName)[0].key;

// Settlement Price provenance label (second line under the amount, and the
// bracketed suffix in the drawer). Keyed on the API's settlementPriceSource.
const SETTLEMENT_SOURCE_LABELS = {
  estimated: 'System Est.',
  confirmed: 'System Settled',
  manual: 'Manual',
};

// ── Query-param builders for server-side fetch ──────────────────────────────
function buildOrderCacheKey(page, platformName, tab, toShipStatus, search, af, sf, so) {
  return JSON.stringify({
    page,
    platformName,
    tab,
    toShipStatus: tab === 'to-ship' ? toShipStatus : '',
    search: (search || '').trim(),
    af,
    sf,
    so,
  });
}

function buildOrderOrdering(sf, so) {
  const FIELD_MAP = {
    id: 'platform_order_no',
    orderTime: 'order_time',
    updateTime: 'update_time',
    linkedStock: 'linked_stockinstance_qty',
    totalPrice: 'total_price',
    basePriceSubtotal: 'base_price_subtotal',
    settlementPrice: 'settlement_price',
    notes: 'notes',
  };
  const field = FIELD_MAP[sf] || 'created_at';
  return `${so === 'desc' ? '-' : ''}${field}`;
}

function buildOrderParams(page, platformName, tab, toShipStatus, search, af, sf, so) {
  const params = new URLSearchParams({ page, page_size: PAGE_SIZE });
  if (platformName) params.append('platform_name', platformName);
  if (tab && tab !== 'all') params.append('tab', tab);
  if (tab === 'to-ship' && toShipStatus) params.append('to_ship_status', toShipStatus);
  if (search && search.trim()) params.append('search', search.trim());
  params.append('ordering', buildOrderOrdering(sf, so));
  if (af) {
    if (af.order_status?.length) params.append('order_status', af.order_status.join(','));
    if (af.fulfillment_method?.length) params.append('fulfillment_method', af.fulfillment_method.join(','));
    if (af.total_price_min) params.append('total_price_min', af.total_price_min);
    if (af.total_price_max) params.append('total_price_max', af.total_price_max);
    if (af.settlement_price_min) params.append('settlement_price_min', af.settlement_price_min);
    if (af.settlement_price_max) params.append('settlement_price_max', af.settlement_price_max);
    if (af.order_items_min) params.append('order_items_min', af.order_items_min);
    if (af.order_items_max) params.append('order_items_max', af.order_items_max);
    if (af.order_time_start) {
      const v = af.order_time_start;
      params.append('order_time_start', v instanceof Date ? v.toISOString() : String(v));
    }
    if (af.order_time_end) {
      const v = af.order_time_end;
      params.append('order_time_end', v instanceof Date ? v.toISOString() : String(v));
    }
    if (af.update_time_start) {
      const v = af.update_time_start;
      params.append('update_time_start', v instanceof Date ? v.toISOString() : String(v));
    }
    if (af.update_time_end) {
      const v = af.update_time_end;
      params.append('update_time_end', v instanceof Date ? v.toISOString() : String(v));
    }
  }
  return params;
}

export default function PlatformOrderPage() {
  const location = useLocation();
  const { pathname } = location;
  const config = platformConfig[pathname] || platformConfig['/platform-order-shopee'];
  const restoreState = location.state?.restoreState;

  const [platformOrders, setPlatformOrders] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  // Fulfillment Method filter options — server returns the distinct values
  // actually present in the table data for this platform.
  const [fulfillmentMethodOptions, setFulfillmentMethodOptions] = useState([]);
  const [orderCounts, setOrderCounts] = useState({});
  const { showLoader, hideLoader } = useLoader();

  const [orderTab, setOrderTab] = useState(restoreState?.orderTab ?? "all");
  const [toShipStatus, setToShipStatus] = useState(
    restoreState?.toShipStatus ?? defaultToShipStatusFor(config.platformName)
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState("orderTime");
  const [sortOrder, setSortOrder] = useState("desc");
  const [selectedIds, setSelectedIds] = useState([]);
  const [actionDropdownOpenId, setActionDropdownOpenId] = useState(null);
  const [productDropdownOpenId, setProductDropdownOpenId] = useState(null);
  const [stockDropdownOpenId, setStockDropdownOpenId] = useState(null);
  const [tooltipContent, setTooltipContent] = useState("");
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [showTooltip, setShowTooltip] = useState(false);
  const [copied, setCopied] = useState(false);
  const [filterPopupOpen, setFilterPopupOpen] = useState(false);
  // ── Order export (ERP-5041) ──────────────────────────────────────────────
  // Kept separate from the table's own filter state: exporting must not disturb
  // whatever the user is currently looking at.
  const [exportOpen, setExportOpen] = useState(false);
  const [exportValues, setExportValues] = useState({});
  const [isExporting, setIsExporting] = useState(false);
  const [branchOptions, setBranchOptions] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(restoreState?.drawerOpen ?? false);
  const [selectedRow, setSelectedRow] = useState(restoreState?.selectedRow ?? null);
  const [drawerTab, setDrawerTab] = useState(restoreState?.drawerTab ?? 'entity-info');

  const actionDropdownRef = useRef(null);
  const productDropdownRef = useRef(null);
  const stockDropdownRef = useRef(null);
  const actionButtonRefs = useRef({});
  const productButtonRefs = useRef({});
  const stockButtonRefs = useRef({});
  const drawerContainerRef = useRef(null);
  const history = useHistory();
  const [alert, setAlert] = useState(null);
  const [showItemAlert, setShowItemAlert] = useState(false);

  const breadcrumbs = ["Order - Platform", config.label];

  // ── Per-tab isolated search & filter state ────────────────────────────────
  const [tabStates, setTabStates] = useState({});
  const currentTabKey = `${pathname}__${orderTab}__${toShipStatus}`;
  const DEFAULT_TAB_STATE = {
    searchInput: '',
    searchTerm: '',
    appliedFilter: null,
    tempValues: {
      order_status: [],
      order_items_min: '',
      order_items_max: '',
      order_time: '',
      update_time: '',
      total_price_min: '',
      total_price_max: '',
      settlement_price_min: '',
      settlement_price_max: '',
      fulfillment_method: [],
    },
  };
  const currentTabState = tabStates[currentTabKey] || DEFAULT_TAB_STATE;
  const searchInput    = currentTabState.searchInput;
  const searchTerm     = currentTabState.searchTerm;
  const appliedFilter  = currentTabState.appliedFilter;
  const tempValues     = currentTabState.tempValues;

  const _updateTabState = (key, updates) =>
    setTabStates(prev => ({ ...prev, [key]: { ...(prev[key] || DEFAULT_TAB_STATE), ...updates } }));

  const setSearchInput   = (val) => _updateTabState(currentTabKey, { searchInput: val });
  const setSearchTerm    = (val) => _updateTabState(currentTabKey, { searchTerm: val });
  const setAppliedFilter = (val) => _updateTabState(currentTabKey, { appliedFilter: val });
  const setTempValues    = (valOrFn) => setTabStates(prev => {
    const cur = prev[currentTabKey] || DEFAULT_TAB_STATE;
    return {
      ...prev,
      [currentTabKey]: {
        ...cur,
        tempValues: typeof valOrFn === 'function' ? valOrFn(cur.tempValues) : valOrFn,
      },
    };
  });

  // ── Prefetch ──────────────────────────────────────────────────────────────
  const prefetchCache = useRef(new Map());
  const prefetchInProgress = useRef(new Set());
  const isFirstSearchRender = useRef(true);
  const isFirstFilterRender = useRef(true);

  // Debounced search (used as the actual API search param)
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // ── Prefetch helper ──────────────────────────────────────────────────────
  async function prefetchOrderPage(page, platformName, tab, toShipStatus, search, af, sf, so, totalCnt) {
    const totalPgs = Math.ceil(totalCnt / PAGE_SIZE);
    if (page > totalPgs) return;
    const key = buildOrderCacheKey(page, platformName, tab, toShipStatus, search, af, sf, so);
    if (prefetchCache.current.has(key) || prefetchInProgress.current.has(key)) return;
    prefetchInProgress.current.add(key);
    try {
      const params = buildOrderParams(page, platformName, tab, toShipStatus, search, af, sf, so);
      const response = await apiHelper(`sales/orders/?${params.toString()}`, 'GET');
      if (!response.ok) return;
      const json = await response.json();
      prefetchCache.current.set(key, { results: json.results || [], count: json.count || 0 });
    } catch (_) { /* silent */ } finally {
      prefetchInProgress.current.delete(key);
    }
  }

  // ── Main reactive fetch ───────────────────────────────────────────────────
  useEffect(() => {
    async function load() {
      const key = buildOrderCacheKey(
        currentPage, config.platformName, orderTab, toShipStatus,
        debouncedSearch, appliedFilter, sortField, sortOrder
      );
      const cached = prefetchCache.current.get(key);
      if (cached) {
        setPlatformOrders(cached.results);
        setTotalCount(cached.count);
        prefetchOrderPage(
          currentPage + 1, config.platformName, orderTab, toShipStatus,
          debouncedSearch, appliedFilter, sortField, sortOrder, cached.count
        );
        return;
      }
      showLoader();
      try {
        const params = buildOrderParams(
          currentPage, config.platformName, orderTab, toShipStatus,
          debouncedSearch, appliedFilter, sortField, sortOrder
        );
        const response = await apiHelper(`sales/orders/?${params.toString()}`, 'GET');
        if (!response.ok) throw new Error('Failed to fetch platform orders');
        const json = await response.json();
        const results = json.results || [];
        const count = json.count || 0;
        prefetchCache.current.set(key, { results, count });
        setPlatformOrders(results);
        setTotalCount(count);
        setFulfillmentMethodOptions(json.fulfillment_methods || []);
        prefetchOrderPage(
          currentPage + 1, config.platformName, orderTab, toShipStatus,
          debouncedSearch, appliedFilter, sortField, sortOrder, count
        );
      } catch (err) {
        console.error('Error fetching platform orders:', err);
        setPlatformOrders([]);
        setTotalCount(0);
      } finally {
        hideLoader();
      }
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pathname, orderTab, toShipStatus, debouncedSearch, appliedFilter, sortField, sortOrder]);

  // ── Debounce search input → debouncedSearch ───────────────────────────────
  useEffect(() => {
    if (isFirstSearchRender.current) { isFirstSearchRender.current = false; return; }
    const t = setTimeout(() => {
      setCurrentPage(1);
      setDebouncedSearch(searchTerm);
    }, 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // ── Clear cache on filter / sort / tab / platform change ──────────────────
  useEffect(() => {
    if (isFirstFilterRender.current) { isFirstFilterRender.current = false; return; }
    setCurrentPage(1);
    prefetchCache.current.clear();
    prefetchInProgress.current.clear();
  }, [pathname, orderTab, toShipStatus, appliedFilter, sortField, sortOrder]);

  // ── Tab-count badges (independent of search/sort/filter/page) ─────────────
  useEffect(() => {
    async function loadCounts() {
      try {
        const params = new URLSearchParams();
        if (config.platformName) params.append('platform_name', config.platformName);
        const response = await apiHelper(`sales/orders/counts/?${params.toString()}`, 'GET');
        if (!response.ok) throw new Error('Failed to fetch order counts');
        const json = await response.json();
        setOrderCounts(json);
      } catch (err) {
        console.error('Error fetching order counts:', err);
        setOrderCounts({});
      }
    }
    loadCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // Function to sanitize integer values (remove decimals)
  const sanitizeIntegerValue = (value) => {
    if (!value) return '';
    // Remove any decimal points or commas and keep only digits
    const sanitized = value.toString().replace(/[.,]/g, '').replace(/[^\d]/g, '');
    return sanitized;
  };

  // Wrapper for setTempValues to sanitize order_items fields
  const setTempValuesWithSanitization = (updates) => {
    if (typeof updates === 'function') {
      setTempValues((prev) => {
        const newValues = updates(prev);
        return {
          ...newValues,
          order_items_min: sanitizeIntegerValue(newValues.order_items_min),
          order_items_max: sanitizeIntegerValue(newValues.order_items_max),
        };
      });
    } else {
      setTempValues({
        ...updates,
        order_items_min: sanitizeIntegerValue(updates.order_items_min),
        order_items_max: sanitizeIntegerValue(updates.order_items_max),
      });
    }
  };

  // Linked Branch choices are every active branch, not only the ones that happen to be
  // linked on the loaded page — same endpoint and response shape LinkStockInstancesModal
  // reads. Fetched once on mount: the list is platform-independent and the branch table
  // does not change while the user sits on this page.
  useEffect(() => {
    apiHelper('entities/branches/?archived=false', 'GET')
      .then(res => (res.ok ? res.json() : []))
      .then(data => {
        const names = (Array.isArray(data) ? data : (data.results ?? []))
          .map(b => b.branch_name)
          .filter(Boolean);
        setBranchOptions(Array.from(new Set(names)).sort((a, b) => a.localeCompare(b)));
      })
      .catch(() => {});
  }, []);

  // Builds the export query. Sending no time range is meaningful: the backend then
  // defaults to today from 00:00:00, which is the documented "no filters" behaviour.
  const handleExport = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const params = new URLSearchParams();
      if (config.platformName) params.set('platform_name', config.platformName);
      const start = exportValues.order_time_start;
      const end   = exportValues.order_time_end;
      if (start) params.set('start_time', start instanceof Date ? start.toISOString() : String(start));
      if (end)   params.set('end_time',   end   instanceof Date ? end.toISOString()   : String(end));
      (exportValues.order_status || []).forEach(v => params.append('order_status[]', v));
      (exportValues.linked_branch || []).forEach(v => params.append('branch_name[]', v));
      (exportValues.fulfillment_method || []).forEach(v => params.append('fulfillment_method[]', v));

      const res = await apiHelper(`sales/orders/export/?${params.toString()}`, 'GET');
      if (res && res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${config.platformName || 'Platform'} Orders.xlsx`;
        link.click();
        window.URL.revokeObjectURL(url);
        setExportOpen(false);
      }
    } catch (err) {
      console.error('Order export failed:', err);
    } finally {
      setIsExporting(false);
    }
  };

  const filterSections = [
    {
      label: "Order Items",
      type: "rangeNormal",
      key: "order_items"
    },
    {
      label: "Order Time",
      key: "order_time",
      type: "dateRangeWithTime",
    },
    {
      label: "Update Time",
      key: "update_time",
      type: "dateRangeWithTime",
    },
    {
      label: "Order Price Subtotal",
      type: "range",
      key: "total_price",
      changeColor: true,
      sgdBackground: "#F8F9FA",
    },
    {
      label: "Settlement Price",
      type: "range",
      key: "settlement_price",
      changeColor: true,
      sgdBackground: "#F8F9FA",
    },
    {
      label: "Fulfillment Method",
      type: "list",
      key: "fulfillment_method",
      showDropdownIcon: true,
      data: fulfillmentMethodOptions,
      // id stays the raw stored value — that is what the backend filters on.
      // Only the visible title is tidied.
      renderItem: (item) => ({ id: item, title: formatFulfillmentMethod(item) }),
    },
  ];

  // "Does this tab have zero orders at all?" — driven by the server-computed
  // per-tab counts, independent of the current search/filter/page state.
  const isCurrentTabEmpty = orderTab === "to-ship"
    ? (orderCounts[toShipStatus] ?? 0) === 0
    : (orderCounts[orderTab] ?? 0) === 0;
  const isInitialTableEmpty = isCurrentTabEmpty;
  // Search/Filter must stay enabled whenever the user is actively searching or
  // has a filter applied, even if that search/filter yields zero results.
  const isSearching = searchTerm?.trim().length > 0;
  const isFilterApplied = !!appliedFilter;
  const isTableEmpty = isInitialTableEmpty && !isSearching && !isFilterApplied;

  const FilterActiveIcon = () => (
    <svg
      style={{ margin: 0 }}
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M20.6325 6.44444C20.7252 6.24267 20.7746 6.02858 20.8107 5.74756C21.0354 4.00089 21.1478 3.12756 21.6356 2.56378C20.1234 2 19.2177 2 17.4063 2H4.59369C2.78227 2 1.87655 2 1.36439 2.56378C0.852232 3.12756 0.964572 4.00089 1.18925 5.74756C1.25487 6.25766 1.36399 6.54725 1.69704 6.94296C2.77293 8.22128 4.74334 10.4951 7.50829 12.5623C7.76095 12.7512 7.92676 13.0599 7.95475 13.4016C8.26623 17.2071 8.55159 19.8068 8.70309 21.0865C8.78256 21.7577 9.52673 22.274 10.14 21.8404C11.1702 21.1118 13.0947 20.2952 13.349 18.938C13.4692 18.2964 13.6451 17.1765 13.8437 15.3333"
        stroke="#0D6EFD"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <g clipPath="url(#clip0_4516_4830)">
        <path
          d="M22.8125 11C22.8125 14.2102 20.2102 16.8125 17 16.8125C13.7898 16.8125 11.1875 14.2102 11.1875 11C11.1875 7.78987 13.7898 5.18753 17 5.18753C20.2102 5.18753 22.8125 7.78987 22.8125 11ZM16.3277 14.0777L20.6402 9.7652C20.7866 9.61877 20.7866 9.38132 20.6402 9.23488L20.1099 8.70456C19.9634 9.5581 19.726 9.5581 19.5795 8.70456L16.0625 12.2215L14.4205 10.5795C14.2741 10.4331 14.0366 10.4331 13.8901 10.5795L13.3598 11.1099C13.2134 11.2563 13.2134 11.4937 13.3598 11.6402L15.7973 14.0777C15.9438 14.2241 16.1812 14.2241 16.3277 14.0777Z"
          fill="#0D6EFD"
        />
      </g>
      <defs>
        <clipPath id="clip0_4516_4830">
          <rect
            width="12"
            height="12"
            fill="white"
            transform="translate(11 5)"
          />
        </clipPath>
      </defs>
    </svg>
  );

  const FilterInactiveIcon = ({ color = "#6C757D" }) => (
    <svg
      style={{ margin: 0 }}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
    >
      <path
        d="M21.6323 6.44444C21.7251 6.24267 21.7744 6.02858 21.8106 5.74756C22.0353 4.00089 22.1476 3.12756 21.6354 2.56378C21.1233 2 20.2176 2 18.4062 2H5.59366C3.78225 2 2.87655 2 2.36439 2.56378C1.85223 3.12756 1.96457 4.00089 2.18925 5.74756C2.25487 6.25766 2.36399 6.54725 2.69703 6.94296C3.77291 8.22128 5.74331 10.4951 8.50823 12.5623C8.76089 12.7512 8.9267 13.0599 8.95469 13.4016C9.26616 17.2071 9.55152 19.8068 9.70302 21.0865C9.78249 21.7577 10.5267 22.274 11.1399 21.8404C12.1701 21.1118 14.0946 20.2952 14.3489 18.938C14.4691 18.2964 14.645 17.1765 14.8436 15.3333"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M18.1113 7.5556V15.3334M22.0001 11.4445L14.2224 11.4445"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const thStyle = {
    textAlign: "left",
    padding: "10px 12px",
    fontSize: 12,
    fontWeight: 600,
  };

  const tdStyle = {
    padding: "12px",
    borderTop: "1px solid #E9ECEF",
    verticalAlign: "top",
    fontSize: 12,
  };

  const badgeYellow = {
    fontSize: 10,
    padding: "3px 8px",
    background: "#FFD600",
    borderRadius: 99,
    fontWeight: 600,
  };

  const Field = ({ label, value }) => (
    <div>
      <div
        style={{
          fontFamily: "Montserrat",
          fontSize: 12,
          fontWeight: 700,
          color: "#212529",
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: "Montserrat",
          fontSize: 12,
          fontWeight: 400,
          color: "#212529",
        }}
      >
        {value || "-"}
      </div>
    </div>
  );

  const copyToClipboard = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, 2000);
  };

  const getDrawerListings = () => {
    console.log(selectedRow);
    if (!selectedRow || !Array.isArray(selectedRow.listings)) return [];
    return selectedRow.listings;
  };

  const DRAWER_PAGE_SIZE = 5;
  const [orderItemsPage, setOrderItemsPage] = useState(1);
  const [stockItemsPage, setStockItemsPage] = useState(1);

  const orderItems = selectedRow?.products || [];
  const orderItemsTotal = orderItems.length;
  const orderItemsTotalPages = Math.ceil(orderItemsTotal / DRAWER_PAGE_SIZE);

  const paginatedOrderItems = orderItems.slice(
    (orderItemsPage - 1) * DRAWER_PAGE_SIZE,
    orderItemsPage * DRAWER_PAGE_SIZE
  );

  const onRowClick = (row) => {
    setSelectedRow(row);
    setDrawerTab('entity-info');
    setDrawerOpen(true);
  };

  // Server already returns the tab-filtered, searched, filtered, sorted,
  // paginated page of results — no client-side filtering needed.
  const pageData = platformOrders;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(prev => prev === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(ids =>
      ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]
    );
  };

  const toggleAll = () => {
    if (selectedIds.length === pageData.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(pageData.map(d => d.id));
    }
  };

  const toggleActionDropdown = (id, event) => {
    event.stopPropagation();
    if (actionDropdownOpenId === id) {
      setActionDropdownOpenId(null);
    } else {
      const btn = actionButtonRefs.current[id];
      if (btn) {
        const rect = btn.getBoundingClientRect();
        setActionDropdownPosition({
          top: rect.bottom + 4,
          right: window.innerWidth - rect.right,
        });
      }
      setActionDropdownOpenId(id);
      setProductDropdownOpenId(null);
      setStockDropdownOpenId(null);
    }
  };

  const toggleProductDropdown = (id, event) => {
    event.stopPropagation();
    if (productDropdownOpenId === id) {
      setProductDropdownOpenId(null);
    } else {
      const btn = productButtonRefs.current[id];
      if (btn) {
        const row = btn.closest('tr');
        const rect = row.getBoundingClientRect();
        setProductDropdownPosition({
          top: rect.bottom + window.scrollY,
          left: rect.left + window.scrollX,
        });
      }
      setProductDropdownOpenId(id);
      setActionDropdownOpenId(null);
      setStockDropdownOpenId(null);
    }
  };

  const toggleStockDropdown = (id, event) => {
    event.stopPropagation();
    if (stockDropdownOpenId === id) {
      setStockDropdownOpenId(null);
    } else {
      const btn = stockButtonRefs.current[id];
      if (btn) {
        const row = btn.closest('tr');
        const rect = row.getBoundingClientRect();
        setStockDropdownPosition({
          top: rect.bottom + window.scrollY,
          left: rect.left + window.scrollX,
        });
      }
      setStockDropdownOpenId(id);
      setActionDropdownOpenId(null);
      setProductDropdownOpenId(null);
    }
  };

  const [actionDropdownPosition, setActionDropdownPosition] = useState({ top: 0, left: 0 });
  const [productDropdownPosition, setProductDropdownPosition] = useState({ top: 0, left: 0 });
  const [stockDropdownPosition, setStockDropdownPosition] = useState({ top: 0, left: 0 });

  const [colWidths, setColWidths] = useState({
    order: 180,
    orderTime: 140,
    updateTime: 140,
    linkedStock: 190,
    totalPrice: 200,
    basePriceSubtotal: 175,
    settlementPrice: 140,
    fulfillmentInfo: 200,
    notes: 180,
  });

  const handleColResizeMouseDown = (e, key, startWidth, minWidth = 80) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const onMouseMove = (moveEvent) => {
      const delta = moveEvent.clientX - startX;
      setColWidths((prev) => ({ ...prev, [key]: Math.max(minWidth, startWidth + delta) }));
    };
    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  const ColResizeHandle = ({ colKey, minWidth }) => (
    <div
      className="col-resize-handle"
      onMouseDown={(e) => handleColResizeMouseDown(e, colKey, colWidths[colKey], minWidth)}
      onClick={(e) => e.stopPropagation()}
      style={{ position: "absolute", right: 0, top: 0, height: "100%", width: "6px", cursor: "col-resize", zIndex: 2, userSelect: "none" }}
    />
  );

  // The sticky Action column sits at right:0, but with fractional scroll
  // positions / fractional table widths the browser can leave a subpixel
  // hairline between the cell and the container's right edge, letting the
  // scrolling columns show through. This strip extends the cell's own
  // background 1px past its right edge to cover that sliver.
  const actionEdgeCover = (
    <div
      aria-hidden="true"
      style={{
        position: "absolute",
        top: 0,
        bottom: 0,
        right: "-1px",
        width: "2px",
        background: "inherit",
        pointerEvents: "none",
      }}
    />
  );

  const orderTableScrollRef = useRef(null);
  const [orderTableScrolledLeft, setOrderTableScrolledLeft] = useState(false);
  const [orderTableScrolledRight, setOrderTableScrolledRight] = useState(false);
  const handleOrderTableScroll = () => {
    const el = orderTableScrollRef.current;
    if (!el) return;
    setOrderTableScrolledLeft(el.scrollLeft > 0);
    setOrderTableScrolledRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  };
  useEffect(() => {
    handleOrderTableScroll();
  }, [pageData]);

  const showTooltipHandler = (text, event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setTooltipContent(text);
    setTooltipPosition({
      top: rect.top,
      left: rect.left + rect.width / 2,
    });
    setShowTooltip(true);
  };

  const hideTooltipHandler = () => {
    setShowTooltip(false);
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        actionDropdownRef.current &&
        !actionDropdownRef.current.contains(event.target) &&
        !Object.values(actionButtonRefs.current).some(btn => btn?.contains(event.target))
      ) {
        setActionDropdownOpenId(null);
      }

      if (
        productDropdownRef.current &&
        !productDropdownRef.current.contains(event.target) &&
        !Object.values(productButtonRefs.current).some(btn => btn?.contains(event.target))
      ) {
        setProductDropdownOpenId(null);
      }

      if (
        stockDropdownRef.current &&
        !stockDropdownRef.current.contains(event.target) &&
        !Object.values(stockButtonRefs.current).some(btn => btn?.contains(event.target))
      ) {
        setStockDropdownOpenId(null);
      }
    }

    function handleScroll(event) {
      if (stockDropdownRef.current && stockDropdownRef.current.contains(event.target)) {
        return;
      }
      if (productDropdownRef.current && productDropdownRef.current.contains(event.target)) {
        return;
      }
      setActionDropdownOpenId(null);
      setProductDropdownOpenId(null);
      setStockDropdownOpenId(null);
    }

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScroll, true);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, []);

  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case "unpaid":
        return "status-badge unpaid";
      case "pending":
        return "status-badge pending";
      case "to process":
        return "status-badge to-process";
      case "processed":
        return "status-badge processed";
      case "shipping":
        return "status-badge shipping";
      case "completed":
        return "status-badge completed";
      case "cancelled":
      case "canceled":
        return "status-badge cancelled";
      default:
        return "status-badge";
    }
  };

  const truncateText = (text, maxLength = 50) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  useEffect(() => {
    const alertData = sessionStorage.getItem("global_alert");
    if (alertData) {
      const alert = JSON.parse(alertData);
      setAlert(alert);
      setShowItemAlert(true);
      sessionStorage.removeItem("global_alert");
      // No explicit refetch needed here: navigating to /platform-manage-order and
      // back remounts this component (see restoreState useState initializers
      // above), so the mount-time order/count fetch effects already load fresh data.
    }
  }, []);

  // Custom handler for FilterDrawer close to ensure order_items values are sanitized
  const handleFilterClose = () => {
    setTempValues((prev) => ({
      ...prev,
      order_items_min: sanitizeIntegerValue(prev.order_items_min),
      order_items_max: sanitizeIntegerValue(prev.order_items_max),
    }));
    setFilterPopupOpen(false);
    setAppliedFilter(null);
  };

  // Custom handler for FilterDrawer apply
  const handleFilterApply = () => {
    console.log(tempValues);
    
    // Sanitize order_items values before applying
    const sanitizedTempValues = {
      ...tempValues,
      order_items_min: sanitizeIntegerValue(tempValues.order_items_min),
      order_items_max: sanitizeIntegerValue(tempValues.order_items_max),
    };
    
    // Update tempValues with sanitized values
    setTempValues(sanitizedTempValues);
    
    const hasAnyFilter =
      sanitizedTempValues.order_status?.length ||
      sanitizedTempValues.order_time_start ||
      sanitizedTempValues.order_time_end ||
      sanitizedTempValues.update_time_start ||
      sanitizedTempValues.update_time_end ||
      sanitizedTempValues.order_items_min ||
      sanitizedTempValues.order_items_max ||
      sanitizedTempValues.settlement_price_min ||
      sanitizedTempValues.settlement_price_max ||
      sanitizedTempValues.total_price_min ||
      sanitizedTempValues.total_price_max ||
      sanitizedTempValues.fulfillment_method?.length;

    if (!hasAnyFilter) {
      setAppliedFilter(null);
      setFilterPopupOpen(false);
      return;
    }
    
    setAppliedFilter({
      order_status: sanitizedTempValues.order_status || [],
      order_time_start: sanitizedTempValues.order_time_start || "",
      order_time_end: sanitizedTempValues.order_time_end || "",
      update_time_start: sanitizedTempValues.update_time_start || "",
      update_time_end: sanitizedTempValues.update_time_end || "",
      order_items_min: sanitizedTempValues.order_items_min || "",
      order_items_max: sanitizedTempValues.order_items_max || "",
      total_price_min: sanitizedTempValues.total_price_min || "",
      total_price_max: sanitizedTempValues.total_price_max || "",
      settlement_price_min: sanitizedTempValues.settlement_price_min || "",
      settlement_price_max: sanitizedTempValues.settlement_price_max || "",
    });
    setFilterPopupOpen(false);
  };

  // Custom handler for FilterDrawer clear
  const handleFilterClear = () => {
    const empty = {
      order_status: [],
      order_time_start: '',
      order_time_end: '',
      update_time_start: '',
      update_time_end: '',
      order_items_min: '',
      order_items_max: '',
      order_time: '',
      update_time: '',
      total_price_min: '',
      total_price_max: '',
      settlement_price_min: '',
      settlement_price_max: '',
      fulfillment_method: [],
    };
    setTempValues(empty);
    setAppliedFilter(null);
  };

  return (
    <div className="company-body new-look stock-wrap">
      {/* Breadcrumbs */}
      <div className="breadcrumb">
        {breadcrumbs.map((crumb, index) => (
          <span key={index} className="breadcrumb-item">
            {crumb}
            {index !== breadcrumbs.length - 1}
          </span>
        ))}
      </div>

      {showItemAlert && alert && (
        <AlertNotification
          type={alert.type}
          action={alert.action}
          recordName={alert.recordName}
          onClose={() => setShowItemAlert(false)}
        />
      )}

      <div className="employee-tabs-container">
        <div>
          <div>
            <div className="tab-content" style={{ background: "white", padding: "20px", borderRadius: "8px" }}>
              {/* Order Tabs */}
              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  marginBottom: "20px",
                  flexWrap: "wrap",
                  padding:"10px 0px",
                  borderBottom: "1px solid #DDD"
                }}
              >
                {["all", "to-link", "unpaid", "to-ship", "shipping", "completed", "cancelled"].map((tab) => {
                  const isActive = orderTab === tab;
                  const tabLabels = {
                    "all": "All",
                    "to-link": "To Link",
                    "unpaid": "Unpaid",
                    "to-ship": "To Ship",
                    "shipping": "Shipping",
                    "completed": "Completed",
                    "cancelled": "Cancelled",
                  };
                  const tabCounts = {
                    "to-link": orderCounts["to-link"],
                    // Unpaid has no sub-statuses, so the tab's own badge is the
                    // whole story for it.
                    "unpaid": orderCounts["unpaid"],
                    "to-ship": orderCounts["to-ship"],
                    "shipping": orderCounts["shipping"],
                  };
                  const count = tabCounts[tab];

                  return (
                    <button
                      key={tab}
                      onClick={() => {
                        setOrderTab(tab);
                        setCurrentPage(1);
                        setSelectedIds([]);
                        if (tab === "to-ship") setToShipStatus(defaultToShipStatusFor(config.platformName));
                      }}
                      style={{
                        display: "flex",
                        height: "35px",
                        padding: "12px 8px",
                        flexDirection: "row",
                        justifyContent: "center",
                        minWidth:"76px",
                        alignItems: "center",
                        gap: "5px",
                        borderRadius: "8px",
                        cursor: "pointer",
                        border: "none",
                        fontFamily: "Montserrat",
                        fontSize: "12px",
                        fontWeight: 600,
                        background: isActive ? "#FFEB00" : "transparent",
                        color: isActive ? "#212529" : "#6C757D",
                      }}
                    >
                      {tabLabels[tab]}
                      {count !== undefined && count > 0 && (
                        <span style={{
                          display: "flex", justifyContent: "center", fontSize: "8px", marginLeft: "2px",
                          height: "16px", minWidth: "16px", padding: " 2px 5px",
                          color: "#FFF",
                          borderRadius: "99px",
                          background: isActive ? "#212529" : "#DEE2E6",
                          alignItems: "center", fontWeight: 600
                        }}>
                          {count}
                        </span>
                      )}
                      {/* Says what the To Link number covers, so a badge of 0 over a
                          non-empty list reads as "nothing new" rather than as a bug. */}
                      {tab === "to-link" && orderCounts["to-link-cutoff"] && (
                        <ToLinkCutoffTooltip
                          cutoff={orderCounts["to-link-cutoff"]}
                          color={isActive ? "#212529" : "#6C757D"}
                        />
                      )}
                    </button>
                  );
                })}
              </div>
              
              {/* Order Status sub-filter for To Ship tab */}
              {orderTab === "to-ship" && (
                <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "20px" }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: "#212529", fontFamily: "Montserrat" }}>Order Status:</span>
                  {toShipSubStatusesFor(config.platformName)
                    .map(({ key, label }) => ({ key, label, count: orderCounts[key] ?? 0 }))
                    .map(({ key, label, count }) => {
                    const isActive = toShipStatus === key;
                    return (
                      <button
                        key={key}
                        onClick={() => { setToShipStatus(key); setCurrentPage(1); }}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "5px",
                          padding: "8px 12px",
                          borderRadius: "99px",
                          fontSize: 12,
                          fontWeight: 600,
                          fontFamily: "Montserrat",
                          cursor: "pointer",
                          border: "1px solid var(--Gray-300, #DEE2E6)",
                          background: isActive ? "#FFEB00" : "transparent",
                          color: isActive? "#212529":"#888F96",
                          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                        }}
                      >
                        {label}
                        {count > 0 && (
                          <span style={{
                            display: "flex", justifyContent: "center", alignItems: "center",
                            fontSize: "8px", height: "16px", minWidth: "16px", padding: "5px",
                            color: "#ffffff", borderRadius: "99px",
                            background: isActive ? "#212529" : "#DEE2E6",
                            fontWeight: 600,
                          }}>
                            {count}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
              
              {/* Search */}
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "16px" }}>
                <div style={{ display: "flex", gap: "8px", width: "100%" }}>
                  <div className="search-box" style={{ width: "300px", position: 'relative', border: isTableEmpty ? "1px solid var(--Gray-400, #CED4DA)" : "1px solid var(--Gray-600, #6C757D)", background: isTableEmpty ? "var(--Gray-100, #F8F9FA)" : "var(--Default-White, #FFF)", pointerEvents: isTableEmpty ? "none" : "auto" }}>
                    <Search size={20} color={isTableEmpty ? "#CED4DA" : "#6C757D"} />
                    {isTableEmpty && (
                      <style>{`.search-box-disabled-po::placeholder { color: #CED4DA !important; }`}</style>
                    )}
                    <input
                      type="text"
                      placeholder="Search..."
                      disabled={isTableEmpty}
                      className={isTableEmpty ? 'search-box-disabled-po' : undefined}
                      style={{ color: isTableEmpty ? '#ADB5BD' : undefined }}
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          setSearchTerm(searchInput);
                          setCurrentPage(1);
                        }
                      }}
                    />
                    {searchInput && (
                      <button
                        onClick={() => { setSearchInput(''); setSearchTerm(''); setCurrentPage(1); }}
                        style={{
                          position: 'absolute',
                          right: '4px',
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '4px',
                        }}
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                  <button
                    className={`icon-btn p-2 filter-btn ${appliedFilter ? "applied" : ""
                      }`}
                    onClick={() =>
                      !isTableEmpty &&
                      setFilterPopupOpen((prev) => !prev)
                    }
                    disabled={isTableEmpty}
                    style={{
                      border: isTableEmpty
                        ? "1px solid var(--Gray-400, #CED4DA)"
                        : appliedFilter
                          ? "1px solid #2C7BE5"
                          : "1px solid var(--Gray-600, #6C757D)",
                      color: isTableEmpty ? "#CED4DA" : appliedFilter ? "#2C7BE5" : "#6C757D",
                      background: isTableEmpty
                        ? "var(--Gray-100, #F8F9FA)"
                        : appliedFilter
                          ? "#CFE2FF"
                          : "var(--Default-White, #FFF)",
                      height: "40px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      pointerEvents: isTableEmpty ? "none" : "auto",
                      cursor: isTableEmpty ? "not-allowed" : "pointer",
                      borderRadius: "8px",
                    }}
                  >
                    {appliedFilter && !isTableEmpty ? (
                      <FilterActiveIcon />
                    ) : (
                      <FilterInactiveIcon color={isTableEmpty ? "#CED4DA" : "#6C757D"} />
                    )}
                  </button>
                  <button
                    onClick={() => setExportOpen(true)}
                    style={{
                      marginLeft: "auto",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      height: "40px",
                      padding: "0 16px",
                      borderRadius: "8px",
                      border: "1px solid #212529",
                      background: "#212529",
                      color: "#FFF",
                      fontFamily: "Montserrat",
                      fontSize: "12px",
                      fontWeight: 600,
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <Upload size={14} strokeWidth={2.5} />
                    Export Order
                  </button>

                  {/* None of the three option lists is narrowed by the current tab,
                      search or page: statuses are the fixed EXPORT_STATUS_OPTIONS,
                      branches are the whole branch table, and fulfillmentOptions reuses
                      the list endpoint's fulfillment_methods (ERP-5042), a
                      platform-scoped distinct() of the column. */}
                  <ExportOrdersDrawer
                    isOpen={exportOpen}
                    onClose={() => setExportOpen(false)}
                    values={exportValues}
                    setValues={setExportValues}
                    statusOptions={statusOptionsFor(config.platformName)}
                    branchOptions={branchOptions}
                    fulfillmentOptions={fulfillmentMethodOptions}
                    onExport={handleExport}
                    isExporting={isExporting}
                  />

                  <FilterDrawer
                    width={400}
                    isOpen={filterPopupOpen}
                    dropdownPlaceholder="Select..."
                    dateRangePlaceholder="Start Time                                   - End Time"
                    onClose={handleFilterClose}
                    sections={filterSections}
                    tempValues={tempValues}
                    setTempValues={setTempValuesWithSanitization}
                    onApply={handleFilterApply}
                    onClear={handleFilterClear}
                    enableAutoSwap={true}
                  />
                </div>
              </div>

              {/* Table */}
              <div
                className="table-wrapper"
                style={{ overflowX: "auto", border: "none", position: "relative" }}
                ref={orderTableScrollRef}
                onScroll={handleOrderTableScroll}
              >
                {pageData.length > 0 && (
                  <table
                    className={`entities-table${orderTableScrolledLeft ? " is-scrolled-left" : ""}${orderTableScrolledRight ? " is-scrolled-right" : ""}`}
                    style={{ width: "100%", tableLayout: "fixed", minWidth: "900px", borderCollapse: "separate", borderSpacing: 0 }}
                  >
                    <colgroup>
                      <col style={{ width: colWidths.order }} />
                      <col style={{ width: colWidths.orderTime }} />
                      <col style={{ width: colWidths.updateTime }} />
                      <col style={{ width: colWidths.linkedStock }} />
                      <col style={{ width: colWidths.totalPrice }} />
                      <col style={{ width: colWidths.basePriceSubtotal }} />
                      <col style={{ width: colWidths.settlementPrice }} />
                      <col style={{ width: colWidths.fulfillmentInfo }} />
                      <col style={{ width: colWidths.notes }} />
                      <col style={{ width: 56 }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th className="sticky-first-col" style={{ backgroundColor: "#E9ECEF", padding: "10px", height: "45px", position: "sticky", left: 0, zIndex: 3, overflow: "hidden" }}>
                          <SortableHeader
                            label="Order"
                            field="id"
                            sortField={sortField}
                            sortOrder={sortOrder}
                            handleSort={handleSort}
                          />
                          <ColResizeHandle colKey="order" minWidth={120} />
                        </th>
                        <th style={{ backgroundColor: "#E9ECEF", padding: "10px", height: "45px", position: "relative", overflow: "hidden" }}>
                          <SortableHeader
                            label="Order Time"
                            field="orderTime"
                            sortField={sortField}
                            sortOrder={sortOrder}
                            handleSort={handleSort}
                          />
                          <ColResizeHandle colKey="orderTime" />
                        </th>
                        <th style={{ backgroundColor: "#E9ECEF", padding: "10px", height: "45px", position: "relative", overflow: "hidden" }}>
                          <SortableHeader
                            label="Update Time"
                            field="updateTime"
                            sortField={sortField}
                            sortOrder={sortOrder}
                            handleSort={handleSort}
                          />
                          <ColResizeHandle colKey="updateTime" />
                        </th>
                        <th style={{ backgroundColor: "#E9ECEF", padding: "10px", height: "45px", position: "relative", overflow: "hidden" }}>
                          <SortableHeader
                            label="Linked StockInstance"
                            field="linkedStock"
                            sortField={sortField}
                            sortOrder={sortOrder}
                            handleSort={handleSort}
                          />
                          <ColResizeHandle colKey="linkedStock" />
                        </th>
                        <th style={{ backgroundColor: "#E9ECEF", padding: "10px", height: "45px", position: "relative", overflow: "hidden" }}>
                          <SortableHeader
                            label="Order Price Subtotal"
                            field="totalPrice"
                            sortField={sortField}
                            sortOrder={sortOrder}
                            handleSort={handleSort}
                          />
                          <ColResizeHandle colKey="totalPrice" />
                        </th>
                        <th style={{ backgroundColor: "#E9ECEF", padding: "10px", height: "45px", position: "relative", overflow: "hidden" }}>
                          <SortableHeader
                            label="Base Price Subtotal"
                            field="basePriceSubtotal"
                            sortField={sortField}
                            sortOrder={sortOrder}
                            handleSort={handleSort}
                          />
                          <ColResizeHandle colKey="basePriceSubtotal" />
                        </th>
                        <th style={{ backgroundColor: "#E9ECEF", padding: "10px", height: "45px", position: "relative", overflow: "hidden" }}>
                          <SortableHeader
                            label="Settlement Price"
                            field="settlementPrice"
                            sortField={sortField}
                            sortOrder={sortOrder}
                            handleSort={handleSort}
                          />
                          <ColResizeHandle colKey="settlementPrice" />
                        </th>
                        <th style={{ backgroundColor: "#E9ECEF", padding: "10px", height: "45px", position: "relative", overflow: "hidden", color: "#212529", fontSize: 12, fontWeight: 700, textAlign: "left" }}>
                          Fulfillment Info
                          <ColResizeHandle colKey="fulfillmentInfo" />
                        </th>
                        <th style={{ backgroundColor: "#E9ECEF", padding: "10px", height: "45px", position: "relative", overflow: "hidden" }}>
                          <SortableHeader
                            label="Notes"
                            field="notes"
                            sortField={sortField}
                            sortOrder={sortOrder}
                            handleSort={handleSort}
                          />
                          <ColResizeHandle colKey="notes" />
                        </th>
                        <th className="sticky-last-col" style={{ textAlign: "center", backgroundColor: "#E9ECEF", padding: "10px", height: "45px", position: "sticky", right: 0, zIndex: 3, overflow: "hidden" }}>
                          Action
                          {actionEdgeCover}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {pageData.map((row, idx) => (
                        <React.Fragment key={row.id}>
                          <tr
                            className={[
                              idx % 2 ? "striped" : "",
                              selectedIds.includes(row.id) ? "row-selected" : "",
                            ].join(" ")}
                            onClick={() => onRowClick(row)}
                          >
                            <td className="sticky-first-col" style={{ backgroundColor: productDropdownOpenId === row.id ? "#FFFEF0" : undefined, overflow: "hidden" }}>
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px", minWidth: 0 }}>
                                <div style={{ display: "flex", flexDirection: "column", minWidth: 0, overflow: "hidden" }}>
                                  <span style={{ fontWeight: 500, marginBottom: "6px", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{row.id}</span>
                                  <div style={{ display: "flex", alignItems: "center", gap: "4px", minWidth: 0 }}>
                                    <span className={getStatusBadgeClass(row.status)} style={{ flexShrink: 0 }}>
                                      {row.status}
                                    </span>
                                    <span style={{ fontSize: "11px", color: "#6C757D", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
                                      {row.itemsCount} Items
                                    </span>
                                  </div>
                                </div>
                                <button
                                  ref={(el) => (productButtonRefs.current[row.id] = el)}
                                  onClick={(e) => toggleProductDropdown(row.id, e)}
                                  style={{
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                    padding: "4px",
                                    display: "flex",
                                    alignItems: "center",
                                    flexShrink: 0,
                                  }}
                                >
                                  <ChevronDown
                                    size={16}
                                    style={{
                                      transform: productDropdownOpenId === row.id ? "rotate(180deg)" : "none",
                                      transition: "transform 0.2s",
                                      color: "#6C757D"
                                    }}
                                  />
                                </button>
                              </div>
                            </td>
                            <td style={{ overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{row.orderTime}</td>
                            <td style={{ overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{row.updateTime}</td>
                            <td style={{ color: row.linkedStock === "0 Linked" ? "#58151C" : "inherit", overflow: "hidden" }}>
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", minWidth: 0 }}>
                                <span style={{ overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{row.linkedStock}</span>
                                <button
                                  ref={(el) => (stockButtonRefs.current[row.id] = el)}
                                  onClick={(e) => toggleStockDropdown(row.id, e)}
                                  style={{
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                    padding: "4px",
                                    marginLeft: "8px",
                                    verticalAlign: "middle",
                                    flexShrink: 0,
                                  }}
                                >
                                  <ChevronDown
                                    size={16}
                                    style={{
                                      transform: stockDropdownOpenId === row.id ? "rotate(180deg)" : "none",
                                      transition: "transform 0.2s",
                                      color: "#6C757D"
                                    }}
                                  />
                                </button>
                              </div>
                            </td>
                            <td style={{ overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{row.totalPrice}</td>
                            <td style={{ overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{row.basePriceSubtotal || 'SGD 0.00'}</td>
                            <td style={{ overflow: "hidden" }}>{(() => {
                              const raw = row.settlementPrice;
                              if (!raw || raw === '-') return '-';
                              const source = row.settlementPriceSource;
                              const sourceLabel = SETTLEMENT_SOURCE_LABELS[source];
                              // A labelled figure is a real figure, zero included (a returned
                              // order can genuinely settle at 0.00). Only unlabelled legacy
                              // rows keep the old "0 means nothing was fetched" reading.
                              if (!sourceLabel) {
                                const num = parseFloat(String(raw).replace(/[^\d.-]/g, ''));
                                return (isNaN(num) || num === 0) ? '-' : raw;
                              }
                              // Same two-line layout as Fulfillment Info: amount, then where
                              // it came from. System Est. = the platform's pre-settlement
                              // estimate and can still move (tooltip carries the platform's
                              // reason); System Settled = the settlement statement; Manual =
                              // typed in via Edit Order.
                              const tip = source === 'estimated'
                                ? (row.settlementPriceNote ? `Estimated by the platform — ${row.settlementPriceNote}` : 'Estimated by the platform; the final amount comes with the settlement statement')
                                : source === 'manual' ? 'Entered manually in ERP' : 'From the platform settlement statement';
                              return (
                                <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                                  <span style={{ overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{raw}</span>
                                  <span
                                    style={{ fontSize: "10px", color: "#6C757D", marginTop: 2, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}
                                    title={tip}
                                  >
                                    {sourceLabel}
                                  </span>
                                </div>
                              );
                            })()}</td>
                            <td style={{ overflow: "hidden" }}>
                              {row.fulfillmentMethod || row.trackingNumber ? (
                                <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                                  <span
                                    style={{ overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}
                                    title={formatFulfillmentMethod(row.fulfillmentMethod) || ''}
                                  >
                                    {formatFulfillmentMethod(row.fulfillmentMethod) || '-'}
                                  </span>
                                  <span
                                    style={{ fontSize: "10px", color: "#6C757D", marginTop: 2, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}
                                    title={row.trackingNumber || ''}
                                  >
                                    {row.trackingNumber || '-'}
                                  </span>
                                </div>
                              ) : '-'}
                            </td>
                            <td style={{ overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }} title={row.notes || ''}>{row.notes || '-'}</td>
                            <td className="sticky-last-col" style={{ textAlign: "center" }}>
                              {actionEdgeCover}
                              <button
                                ref={(el) => (actionButtonRefs.current[row.id] = el)}
                                className="action-btn"
                                onClick={(e) => toggleActionDropdown(row.id, e)}
                                style={{
                                  background: "none",
                                  border: "none",
                                  cursor: "pointer",
                                  padding: "8px",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  borderRadius: "4px",
                                }}
                              >
                                <MoreHorizontal size={20} color="#6C757D" />
                              </button>
                            </td>
                          </tr>

                          {/* Product Details Dropdown */}
                          {productDropdownOpenId === row.id && (
                            <tr>
                              <td colSpan="10" style={{ padding: 0, borderBottom: "none" }}>
                                <div
                                  ref={productDropdownRef}
                                  className="product-dropdown"
                                  style={{
                                    position: "fixed",
                                    top: productDropdownPosition.top,
                                    left: productDropdownPosition.left,
                                    background: "white",
                                    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
                                    zIndex: 1000,
                                    padding: "10px",
                                    maxHeight: '300px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                  }}
                                >
                                  <table style={{ width: "100%", borderCollapse: "collapse", display: 'block', overflowY: 'auto' }}>
                                    <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                                      <tr style={{ backgroundColor: "#FFEB00" }}>
                                        <th style={{ padding: "10px", textAlign: "left", fontSize: "12px", fontWeight: "700", width: "230px" }}>Product</th>
                                        <th style={{ padding: "10px", textAlign: "left", fontSize: "12px", fontWeight: "700",minWidth:"122px" }}>
                                          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                            Unit Price
                                            <div
                                              style={{ position: "relative", display: "inline-flex", alignItems: "center", cursor: "default" }}
                                              onMouseEnter={(e) => showTooltipHandler("The Unit Price of every item in the Order, before discount. Transmitted from e-Commerce platform.", e)}
                                              onMouseLeave={hideTooltipHandler}
                                            >
                                              <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none">
                                                <g clipPath="url(#clip0_2150_26736)">
                                                  <path fillRule="evenodd" clipRule="evenodd" d="M5 9.375C6.16032 9.375 7.27312 8.91406 8.09359 8.09359C8.91406 7.27312 9.375 6.16032 9.375 5C9.375 3.83968 8.91406 2.72688 8.09359 1.90641C7.27312 1.08594 6.16032 0.625 5 0.625C3.83968 0.625 2.72688 1.08594 1.90641 1.90641C1.08594 2.72688 0.625 3.83968 0.625 5C0.625 6.16032 1.08594 7.27312 1.90641 8.09359C2.72688 8.91406 3.83968 9.375 5 9.375ZM5 10C6.32608 10 7.59785 9.47322 8.53553 8.53553C9.47322 7.59785 10 6.32608 10 5C10 3.67392 9.47322 2.40215 8.53553 1.46447C7.59785 0.526784 6.32608 0 5 0C3.67392 0 2.40215 0.526784 1.46447 1.46447C0.526784 2.40215 0 3.67392 0 5C0 6.32608 0.526784 7.59785 1.46447 8.53553C2.40215 9.47322 3.67392 10 5 10Z" fill="#5E6E82" />
                                                  <path d="M5.58122 4.11719L4.14997 4.29656L4.09872 4.53406L4.37997 4.58594C4.56372 4.62969 4.59997 4.69594 4.55997 4.87906L4.09872 7.04656C3.97747 7.60719 4.16435 7.87094 4.60372 7.87094C4.94435 7.87094 5.33997 7.71344 5.51935 7.49719L5.57434 7.23719C5.44934 7.34719 5.26684 7.39094 5.1456 7.39094C4.97372 7.39094 4.91122 7.27031 4.9556 7.05781L5.58122 4.11719Z" fill="#5E6E82" />
                                                  <path d="M5 3.4375C5.34518 3.4375 5.625 3.15768 5.625 2.8125C5.625 2.46732 5.34518 2.1875 5 2.1875C4.65482 2.1875 4.375 2.46732 4.375 2.8125C4.375 3.15768 4.65482 3.4375 5 3.4375Z" fill="#5E6E82" />
                                                </g>
                                                <defs>
                                                  <clipPath id="clip0_2150_26736">
                                                    <rect width="10" height="10" fill="white" />
                                                  </clipPath>
                                                </defs>
                                              </svg>
                                            </div>
                                          </div>
                                        </th>
                                        <th style={{ padding: "12px 16px", textAlign: "left", fontSize: "12px", fontWeight: "700",minWidth:"57px" }}>Qty</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {row.products.map((product, index) => (
                                        <tr key={index} style={{ borderBottom: index < row.products.length - 1 ? "1px solid #E9ECEF" : "none",padding:'10px' }}>
                                          <td style={{ padding: "10px" }}>
                                            <div
                                              onMouseOver={(e) => showTooltipHandler(product.name, e)}
                                              onMouseLeave={hideTooltipHandler}
                                              className="platform-order-product-name"
                                            >
                                              {product.name}
                                            </div>
                                            <div
                                              onMouseOver={(e) => showTooltipHandler('Variation: ' + product.variation, e)}
                                              onMouseLeave={hideTooltipHandler}
                                              style={{ fontSize: "12px", color: "#6C757D", marginTop: "2px", cursor: "default" }}
                                            >
                                              Variation: {truncateText(product.variation, 30)}
                                            </div>
                                          </td>
                                          <td style={{ padding: "10px", fontSize: "12px" ,minWidth:"122px"}}>{product.unitPrice}</td>
                                          <td style={{ padding: "10px", fontSize: "12px",textAlign:'end',minWidth:"57px" }}>{product.qty}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </td>
                            </tr>
                          )}

                          {/* Stock Instance Dropdown */}
                          {stockDropdownOpenId === row.id && (
                            <tr>
                              <td colSpan="10" style={{ padding: 0, borderBottom: "none" }}>
                                <div
                                  ref={stockDropdownRef}
                                  className="stock-dropdown"
                                  style={{
                                    position: "fixed",
                                    top: stockDropdownPosition.top,
                                    left: '50%',
                                    background: "white",
                                    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
                                    zIndex: 1000,
                                    padding: "10px",
                                    maxHeight:'300px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                  }}
                                >
                                  {row.stockInstances && row.stockInstances.length > 0 ? (
                                    <table style={{ width: "100%", borderCollapse: "collapse", display: 'block', overflowY: 'auto' }}>
                                      <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                                        <tr style={{ backgroundColor: "#FFEB00" }}>
                                          <th style={{ padding: "10px", textAlign: "left", fontSize: "12px", fontWeight: "700" }}>Stock</th>
                                          <th style={{ padding: "10px", textAlign: "left", fontSize: "12px", fontWeight: "700",width:'auto' }}>StockInstance</th>
                                          <th style={{ padding: "10px", textAlign: "left", fontSize: "12px", fontWeight: "700" }}>Qty</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {row.stockInstances.map((stock, index) => (
                                          <tr key={index} style={{ borderBottom: index < row.stockInstances.length - 1 ? "1px solid #E9ECEF" : "1px solid #E9ECEF" }}>
                                            <td style={{ padding: "10px" }}>
                                              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                                <img
                                                  src={stock.image || noImage}
                                                  alt={stock.stockName}
                                                  style={{
                                                    width: "32px",
                                                    height: "32px",
                                                    objectFit: "cover",
                                                    borderRadius: "4px"
                                                  }}
                                                />
                                                <div>
                                                  <div style={{ fontSize: "12px", fontWeight: "400" }}>
                                                      <div
                                                        title={stock.stockName}
                                                        style={{ 
                                                          width:'150px',
                                                          cursor: "default",
                                                          whiteSpace: "nowrap",
                                                          overflow: "hidden",
                                                          textOverflow: "ellipsis",
                                                          display: "block",
                                                        }}
                                                      >
                                                        {stock.stockName}
                                                      </div>
                                                    </div>
                                                  <div style={{ fontWeight: 400, fontSize: "10px", color: "#6C757D" }}>
                                                    {stock.stockSku}{stock.stockModelNumber ? ` | ${stock.stockModelNumber}` : ''}
                                                  </div>
                                                  <div style={{ display: "flex", gap: "4px", marginTop: "4px" }}>
                                                    <span style={{
                                                      padding: "2px 5px",
                                                      borderRadius: "12px",
                                                      fontSize: "8px",
                                                      fontWeight: "600",
                                                      backgroundColor: "#FFEB00",
                                                      color: "#212529",
                                                      alignContent:'center'
                                                    }}>
                                                      {stock.condition}
                                                    </span>
                                                    <span style={{
                                                      padding: "2px 5px",
                                                      borderRadius: "12px",
                                                      fontSize: "8px",
                                                      fontWeight: "600",
                                                      backgroundColor: "#FFEB00",
                                                      color: "#212529",
                                                      alignContent:'center'
                                                    }}>
                                                      {stock.condition_type}
                                                    </span>
                                                  </div>
                                                </div>
                                              </div>
                                            </td>
                                            <td style={{ padding: "10px", fontSize: "11px", color: "#6C757D",width:'auto'  }}>
                                              <div>
                                                <div style={{ fontSize: "12px", fontWeight: "500",width:'auto' }}>
                                                  <span>
                                                    {stock.skuId}
                                                  </span>
                                                </div>
                                                <div style={{ fontWeight: 400, fontSize: "10px", color: "#6C757D" }}>
                                                  {stock.branch} | SGD: {stock.price}
                                                </div>
                                              </div>
                                            </td>
                                            <td style={{ padding: "10px", fontSize: "12px",textAlign:'end' }}>{stock.instanceQty}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  ) : (
                                    <div className="empty-state" style={{
                                      width: "449px",
                                      padding: "10px 10px 20px 10px"
                                    }}>
                                      <div className="empty-state-content">
                                        <div className="icon">
                                          <img
                                            src={noStockInstanceImage}
                                            alt="empty"
                                            style={{ width: 120, height: 120, margin: "11px 0px" }}
                                          />
                                        </div>
                                        <h1 className="no-item-title" style={{ margin: "10px 0px", fontFamily: "Montserrat" }}>No Linked StockInstances</h1>
                                        <p className="no-item-text" style={{ marginBottom: "0px" }}>No StockInstances Linked to this Order.</p>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                )}
                <div className="drawer-container" ref={drawerContainerRef}>
                  {drawerOpen && selectedRow && (
                    <PlatformOrderDrawer
                      drawerOpen={drawerOpen}
                      selectedRow={selectedRow}
                      setDrawerOpen={setDrawerOpen}
                      setSelectedRow={setSelectedRow}
                      platformName={config.platformName}
                    />
                  )}
                </div>

                {/* Action Dropdown Menu */}
                {actionDropdownOpenId && (
                  <div
                    ref={actionDropdownRef}
                    className="action-dropdown-menu"
                    style={{
                      position: "fixed",
                      top: actionDropdownPosition.top,
                      right: actionDropdownPosition.right,
                      background: "white",
                      boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
                      zIndex: 1000,
                      minWidth: "160px",
                      borderRadius: "8px",
                      padding: "0",
                      fontFamily: "Montserrat",
                      fontSize: "14px",
                      fontWeight: 400
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="dropdown-item" style={{ padding: "0px" }}>
                      <button onClick={() => {
                        const row = pageData.find(r => r.id === actionDropdownOpenId);
                        setActionDropdownOpenId(null);
                        if (row) {
                          onRowClick(row);
                        }
                      }}
                        style={{
                          fontFamily: "Montserrat",
                          fontSize: "14px",
                          fontWeight: 400, padding: "10px"
                        }}>
                        View Details
                      </button>
                    </div>
                    <div className="dropdown-item" style={{ padding: "0px",borderRadius:'8px' }}>
                      <button onClick={() => {
                        const row = pageData.find(r => r.id === actionDropdownOpenId);
                        history.push("/platform-manage-order", {
                          order: row,
                          platformLabel: config.label,
                          fromPath: pathname,
                          restoreState: { orderTab, toShipStatus, drawerOpen, selectedRow, drawerTab },
                        });
                      }}
                      disabled={["cancelled","canceled"].includes(pageData.find(r => r.id === actionDropdownOpenId)?.status?.toLowerCase())}
                        style={{
                          fontFamily: "Montserrat",
                          fontSize: "14px",
                          fontWeight: 400,
                          padding: "10px",
                          opacity: ["cancelled","canceled"].includes(pageData.find(r => r.id === actionDropdownOpenId)?.status?.toLowerCase()) ? 0.5 : 1,
                          cursor: ["cancelled","canceled"].includes(pageData.find(r => r.id === actionDropdownOpenId)?.status?.toLowerCase()) ? "not-allowed" : "pointer"
                        }}
                      >
                        Manage
                      </button>
                    </div>
                  </div>
                )}

                {/* Tooltip */}
                {showTooltip && (
                  <div
                    style={{
                      position: "fixed",
                      top: tooltipPosition.top,
                      left: tooltipPosition.left,
                      transform: "translate(-50%, calc(-100% - 8px))",
                      background: "#495057",
                      color: "white",
                      padding: "16px",
                      borderRadius: "8px",
                      zIndex: 2000,
                      minWidth: "100px",
                      maxWidth: "300px",
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      pointerEvents: "none",
                      fontFamily: "Montserrat",
                      fontSize: "12px",
                      fontStyle: "normal",
                      fontWeight: 400,
                      lineHeight: "normal"
                    }}
                  >
                    {tooltipContent}
                    <div style={{ width: 0, height: 0, borderRight: '15px solid transparent', borderBottom: '15px solid transparent', borderTop: '15px solid #495057', position: 'absolute', borderLeft: '15px solid transparent', left: '50%',top:'98%', transform: 'translateX(-50%)' }}></div>
                  </div>
                )}
              </div>
              {totalCount > 0 && (
                <div className="pagination" style={{ marginTop: "20px", paddingTop: "0px" }}>
                  <div className="pagination-info" style={{ color: '#212529' }}>
                    {(currentPage - 1) * PAGE_SIZE + 1} -{" "}
                    {Math.min(currentPage * PAGE_SIZE, totalCount)} of{" "}
                    {totalCount}
                  </div>
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                  />
                </div>
              )}

              {totalCount === 0 && searchTerm && (
                <div className="empty-state">
                  <div className="empty-state-content">
                    <div class="icon">
                      <img
                        src={emptySearchImg}
                        alt="avatar"
                        className="avatar"
                        style={{
                          width: 120,
                          height: 120,
                          verticalAlign: "middle",
                        }}
                      />
                    </div>
                    <h1 class="no-item-title mt-2" style={{
                      fontFamily: "Montserrat",
                      fontSize: "20px",
                      fontStyle: "normal",
                      fontWeight: 600,
                      lineHeight: "normal"
                    }}>
                      No Orders Found
                    </h1>
                    <p class="no-item-text" style={{
                      color: "var(--Gray-600, #6C757D)",
                      fontFamily: "Montserrat",
                      fontSize: "14px",
                      fontStyle: "normal",
                      fontWeight: 400,
                      margin: "0px"
                    }}>
                      No results match what you're looking for
                    </p>
                  </div>
                </div>
              )}
              {pageData.length === 0 && !searchTerm && (
                <div className="empty-state">
                  <div className="empty-state-content">
                    <div class="icon">
                      <img
                        src={noBrandImage}
                        alt="avatar"
                        className="avatar"
                        style={{
                          width: 120,
                          height: 120,
                          verticalAlign: "middle",
                        }}
                      />
                    </div>
                    <h1 class="no-item-title mt-2" style={{
                      fontFamily: "Montserrat",
                      fontSize: "20px",
                      fontStyle: "normal",
                      fontWeight: 600,
                      lineHeight: "normal"
                    }}>
                      No Orders Found
                    </h1>
                    <p class="no-item-text" style={{
                      color: "var(--Gray-600, #6C757D)",
                      fontFamily: "Montserrat",
                      fontSize: "14px",
                      fontStyle: "normal",
                      fontWeight: 400,
                      margin: "0px"
                    }}>
                      Nothing here yet.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add necessary CSS */}
      <style jsx>{`
        .status-badge {
          display: inline-block;
          padding: 2px 5px;
          font-size: 8px;
          font-weight: 600;
          width: fit-content;
          border-radius: 99px;
        }
        .status-badge.unpaid {
          color: #6C757D;
          border: 1px solid #ADB5BD;
          background: #F8F9FA;
        }
        .status-badge.pending {
          color: #ADB5BD;
          border: 1px solid #ADB5BD;
          background: #F8F9FA;
        }
        .status-badge.to-process {
          color: #6C757D;
          border: 1px solid var(--Utilised-text-2, #6C757D);
          background: var(--Utilised-background-2, #F8F9FA);
        }
        .status-badge.processed {
          color: #2C7BE5;
          border: 1px solid var(--Utilised-blue-2, #2C7BE5);
          background: var(--Utilised-blue-1, #CFE2FF);
        }
        .status-badge.shipping {
          color: #CC9A06;
          border: 1px solid var(--Utilised-warning-2, #CC9A06);
          background: var(--Utilised-warning-1, #FFF3CD);
        }
        .status-badge.completed {
          border: 1px solid #146C43;
          background: #D1E7DD;
          color: #146C43;
        }
        .status-badge.cancelled {
          border: 1px solid var(--Utilised-error-2, #B02A37);
          background: var(--Utilised-error-1, #F8D7DA);
          color: #B02A37;
        }
        .table-wrapper {
          position: relative;
          background: white;
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .table-wrapper::-webkit-scrollbar {
          display: none;
        }
        .entities-table {
          width: 100%;
          border-collapse: collapse;
        }
        .entities-table th {
          font-size: 12px;
          font-weight: 700;
          color: #212529;
          border-bottom: 1px solid #E9ECEF;
          white-space: nowrap;
        }
        .entities-table td {
          padding: 10px;
          font-size: 12px;
          color: #212529;
          border-bottom: 1px solid #E9ECEF;
        }
     
        .entities-table tr.striped {
          background-color: #F8F9FA;
        }
        .entities-table tr.row-selected {
          background-color: #E7F5FF;
        }
        .entities-table td.sticky-first-col {
          position: sticky;
          left: 0;
          z-index: 2;
          background-color: #ffffff;
        }
        .entities-table td.sticky-last-col {
          position: sticky;
          right: 0;
          z-index: 2;
          background-color: #ffffff;
        }
        .entities-table tr.striped td.sticky-first-col,
        .entities-table tr.striped td.sticky-last-col {
          background-color: #F8F9FA;
        }
        .entities-table tr.row-selected td.sticky-first-col,
        .entities-table tr.row-selected td.sticky-last-col {
          background-color: #E7F5FF;
        }

        /* Column resize grip mark */
        .col-resize-handle::after {
          content: "";
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          height: 50%;
          left: 3px;
          width: 2px;
          background: #DEE2E6;
        }

        /* Sticky-column edge shadows — only shown while there's actually
           scrolled content hidden behind the sticky column, and forced to
           overflow:visible so the shadow can escape the cell despite the
           overflow:hidden used elsewhere for text truncation. */
        .entities-table th.sticky-first-col,
        .entities-table td.sticky-first-col,
        .entities-table th.sticky-last-col,
        .entities-table td.sticky-last-col {
          overflow: visible !important;
        }
        .entities-table.is-scrolled-left th.sticky-first-col::after,
        .entities-table.is-scrolled-left td.sticky-first-col::after {
          content: "";
          position: absolute;
          top: 0;
          right: -8px;
          width: 8px;
          height: 100%;
          pointer-events: none;
          z-index: 1;
          background: linear-gradient(to right, rgba(0,0,0,0.12), rgba(0,0,0,0));
        }
        .entities-table.is-scrolled-right th.sticky-last-col::before,
        .entities-table.is-scrolled-right td.sticky-last-col::before {
          content: "";
          position: absolute;
          top: 0;
          left: -8px;
          width: 8px;
          height: 100%;
          pointer-events: none;
          z-index: 1;
          background: linear-gradient(to left, rgba(0,0,0,0.12), rgba(0,0,0,0));
        }
        .search-box {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          border: 1px solid #DEE2E6;
          border-radius: 8px;
          background: white;
        }
        .search-box input {
          border: none;
          outline: none;
          width: 100%;
          font-size: 14px;
          background: transparent;
        }
        .action-btn {
          background: none;
          border: none;
          cursor: pointer;
          padding: 8px;
          border-radius: 4px;
        }
        .action-btn:hover {
        background-color: #F1F3F5;
        }
        .action-dropdown-menu{
         background: white;
         padding: 4px 8px;
        }
        .dropdown-item {
          background: white;
        }
        .dropdown-item:hover {
          background: white !important;
        }
        .dropdown-item button {
          width: 100%;
          border: none;
          background: white;
          text-align: left;
          cursor: pointer;
          font-size: 12px;
          color: #212529;
          border-radius: 4px;
        }
        .dropdown-item button:hover {
          background: white !important;
        }
        .pagination {
          display: flex;
          justify-content: end;
          align-items: center;
          padding: 16px 0;
        }
        .pagination-info {
          font-size: 12px;
          color: #6C757D;
        }
        .pagination-control {
          display: flex;
          gap: 4px;
        }
        .pagination-control button {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          border: 1px solid #DEE2E6;
          border-radius: 4px;
          background: white;
          cursor: pointer;
          color: #212529;
        }
        .pagination-control button.active {
          background: #FFEB00;
          border-color: #FFEB00;
          font-weight: 600;
        }
        .pagination-control button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .sorticon{
          line-height:1.3!important;
        }
      `}</style>
    </div>
  );
}